import "@xyflow/react/dist/style.css";

import {
  Background,
  Node as NodeBase,
  OnConnectEnd,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  getConnectedEdges,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import { apiUserGetPartners, apiUserUpload } from "@/api";
import { DatePicker, Divider, Modal, message } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";

import TextUpdaterNode from "@/components/TextUpdateNode";
import { orpcClient } from "@/lib/orpc-client";
import { asSingle, single } from "@/lib/orpc-batch";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const updateProject = asSingle(orpcClient.project.update);
const createProjectNode = asSingle(orpcClient.projectNode.create);
const updateProjectNode = asSingle(orpcClient.projectNode.update);
const deleteProjectNode = asSingle(orpcClient.projectNode.delete);

const initialNodes: Node[] = [
  {
    id: "0",
    documentId: "0",
    type: "building",
    data: { label: "Node A", name: "Alpha", type: "building" },
    position: { x: 0, y: 50 },
  },
];

interface NodeData extends Record<string, unknown> {
  label: string;
  name: string;
  type: NodeType;
  designBOM?: DesignBOM[];
  childNodes?: string[];
  workItems?: any[];
  steelAssemblyList?: any[];
  steelAssemblyStats?: Record<string, any>;
  priority?: number;
  startTime?: string;
  endTime?: string;
}

type NodeType = "building" | "zone" | "batch" | "package";

interface Node extends NodeBase {
  documentId: string;
  type: NodeType;
  data: NodeData;
}

interface Edge {
  id: string;
  source: string;
  target: string;
}

const validConnections: Record<NodeType, NodeType[]> = {
  building: ["zone"],
  zone: ["batch"],
  batch: ["package"],
  package: [], // No children allowed
};

// const defaultNodeName: Record<NodeType, string> = {
//   // zone: "分區",
//   // batch: "分批",
//   // package: "構件包",
//   // building: "建築",
//   zone: t("zone"),
//   batch: t("batch"),
//   package: t("package"),
//   building: t("building"),
// };

let idBase = 1;
const getId = () => `${idBase++}`;
const nodeOrigin = [0.5, 0] as [number, number];
const childCounts: Record<string, number> = {}; // Track child node counts for each parent

interface NodeJsonData {
  nodes?: Node[];
  edges?: Edge[];
}

interface DesignBOM {
  documentId: string;
}

const planSignature = (planNodes: any[], planEdges: any[]) =>
  JSON.stringify({
    nodes: (planNodes || [])
      .map((node: any) => ({
        id: node?.documentId || node?.id || "",
        type: node?.type ?? null,
        x: Math.round(node?.position?.x ?? 0),
        y: Math.round(node?.position?.y ?? 0),
        name: node?.data?.name ?? node?.data?.label ?? null,
        parentNode: node?.data?.parentNode ?? null,
        childNodes: [...(node?.data?.childNodes || [])].sort(),
        priority: node?.data?.priority ?? null,
        startTime: node?.data?.startTime ?? null,
        endTime: node?.data?.endTime ?? null,
      }))
      .sort((a: any, b: any) => String(a.id).localeCompare(String(b.id))),
    edges: (planEdges || [])
      .map((edge: any) => `${edge?.source}->${edge?.target}`)
      .sort(),
  });

interface NodeProps {
  data: {
    label: string;
    name: string;
    type: string;
    documentId?: string;
    childNodes?: string[];
    workItems?: any[];
    steelAssemblyList?: any[];
    steelAssemblyStats?: Record<string, any>;
  };
  isConnectable: boolean;
  id: string;
  type: string;
}

const COLOR_PALETTE = [
  "#ef4444", // red
  "#f97316", // orange
  "#f59e0b", // amber
  "#eab308", // yellow
  "#84cc16", // lime
  "#22c55e", // green
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#ec4899", // pink
];

const ProductionPlanFlow = ({
  designBomData,
  layerColor,
  showQRCode,
  isPriorityModalOpen,
  setIsPriorityModalOpen,
  onSelectedNodeChange,
  reloadKey,
}: {
  designBomData: DesignBOM[];
  layerColor: Record<NodeType, string>;
  showQRCode: boolean;
  isPriorityModalOpen: boolean;
  setIsPriorityModalOpen: (open: boolean) => void;
  onSelectedNodeChange?: (
    node: { id: string; name: string; type?: string } | null
  ) => void;
  reloadKey?: number;
}) => {
  const { t } = useTranslation();
  const reactFlowWrapper = useRef(null);
  const [fileUpload, setFileUpload] = useState<any[]>([]);
  const [designBOM, setDesignBOM] = useState<any[]>([]);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(initialNodes);
  const [nodeIdMap, setNodeIdMap] = useState<Record<string, string>>({});
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { screenToFlowPosition } = useReactFlow();
  const { id = "" } = useParams();
  const saveTimeoutRef = useRef<number | null>(null);
  const [pendingDeleteNodes, setPendingDeleteNodes] = useState<Node[]>([]);
  const [pendingDeleteEdges, setPendingDeleteEdges] = useState<Edge[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
const [priorityInputs, setPriorityInputs] = useState<Record<string, string>>({});
  const [startTimeInputs, setStartTimeInputs] = useState<Record<string, string>>({});
  const [endTimeInputs, setEndTimeInputs] = useState<Record<string, string>>({});

  const saveNodePositions = useCallback(
    async (updatedNodes: Node[]) => {
      try {
        await updateProject({
          id: id || "",
          productionPlan: {
            nodes: updatedNodes.map((node) => {
              const { designBOM, ...dataWithoutDesignBOM } = node.data;
              return {
                id: node.id,
                documentId: node.documentId,
                type: node.type,
                position: node.position,
                data: {
                  ...dataWithoutDesignBOM,
                },
              };
            }),
            edges: edges,
          },
        });
      } catch (error) {
        console.error("Error saving node position:", error);
      }
    },
    [id, edges]
  );

  const handleNodesChange = useCallback(
    (changes: any[]) => {
      // Filter out delete changes for root node
      const filteredChanges = changes.filter((change: any) => {
        if (change.type === "remove") {
          const node = nodes.find((n) => n.id === change.id);
          if (node) {
            const isRootNode =
              node.id === "0" ||
              (node.type === "building" && !node.data.parentNode);
            return !isRootNode;
          }
        }
        return true;
      });

      // Apply the changes immediately to the UI
      onNodesChange(filteredChanges);

      // Check for position changes and debounce API call
      const positionChanges = filteredChanges.filter(
        (change: any) => change.type === "position"
      );

      if (positionChanges.length > 0) {
        // Clear existing timeout
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        // Set new timeout to save positions after user stops dragging
        saveTimeoutRef.current = setTimeout(() => {
          // Get the updated nodes with new positions
          const updatedNodes = nodes.map((node) => {
            const positionChange = positionChanges.find(
              (change: any) => change.id === node.id
            );
            if (positionChange) {
              return {
                ...node,
                position: positionChange.position,
              };
            }
            return node;
          });

          saveNodePositions(updatedNodes);
        }, 500); // Wait 500ms after last position change
      }
    },
    [nodes, onNodesChange, saveNodePositions]
  );

  useEffect(() => {
    if (!isPriorityModalOpen) return;
    const inputs: Record<string, string> = {};
    const startInputs: Record<string, string> = {};
    const endInputs: Record<string, string> = {};
    nodes.forEach((node) => {
      inputs[node.id] = node.data.priority !== undefined ? String(node.data.priority) : "";
      startInputs[node.id] = node.data.startTime ? String(node.data.startTime) : "";
      endInputs[node.id] = node.data.endTime ? String(node.data.endTime) : "";
    });
    setPriorityInputs(inputs);
    setStartTimeInputs(startInputs);
    setEndTimeInputs(endInputs);
  }, [isPriorityModalOpen]);

  const savePriorities = useCallback(() => {
    const filled = Object.entries(priorityInputs).filter(([, v]) => v !== "");
    const values = filled.map(([, v]) => Number(v));
    if (values.length !== new Set(values).size) {
      message.error(t("productionPlanFlow.priorityDuplicate"));
      return;
    }
    for (const node of nodes) {
      const start = startTimeInputs[node.id];
      const end = endTimeInputs[node.id];
      if (start && end && dayjs(end).isBefore(dayjs(start))) {
        message.error(t("productionPlanFlow.timeInvalid"));
        return;
      }
    }
    const updatedNodes = nodes.map((node) => {
      const val = priorityInputs[node.id];
      const start = startTimeInputs[node.id];
      const end = endTimeInputs[node.id];
      const newData = { ...node.data };
      if (val && val !== "") {
        newData.priority = Number(val);
      } else {
        delete newData.priority;
      }
      if (start && start !== "") {
        newData.startTime = start;
      } else {
        delete newData.startTime;
      }
      if (end && end !== "") {
        newData.endTime = end;
      } else {
        delete newData.endTime;
      }
      return { ...node, data: newData };
    });
    setNodes(updatedNodes);
    saveNodePositions(updatedNodes);
    setIsPriorityModalOpen(false);
  }, [priorityInputs, startTimeInputs, endTimeInputs, nodes, saveNodePositions, t]);

  const defaultNodeName: Record<NodeType, string> = {
    // zone: "分區",
    // batch: "分批",
    // package: "構件包",
    // building: "建築",
    zone: t("productionPlanFlow.zone"),
    batch: t("productionPlanFlow.batch"),
    package: t("productionPlanFlow.package"),
    building: t("productionPlanFlow.building"),
  };

  const handleNodeUpdate = useCallback((nodeId: string, newName: string) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.documentId === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              label: newName,
              name: newName,
            },
          };
        }
        return node;
      })
    );
  }, []);

  // Memoize nodeTypes to prevent recreation on every render
  const memoizedNodeTypes = useMemo(
    () => ({
      building: (props: NodeProps) => (
        <TextUpdaterNode
          {...props}
          onNodeUpdate={handleNodeUpdate}
          borderColor={layerColor.building}
          showQRCode={showQRCode}
        />
      ),
      zone: (props: NodeProps) => (
        <TextUpdaterNode
          {...props}
          onNodeUpdate={handleNodeUpdate}
          borderColor={layerColor.zone}
          showQRCode={showQRCode}
        />
      ),
      batch: (props: NodeProps) => (
        <TextUpdaterNode
          {...props}
          onNodeUpdate={handleNodeUpdate}
          borderColor={layerColor.batch}
          showQRCode={showQRCode}
        />
      ),
      package: (props: NodeProps) => (
        <TextUpdaterNode
          {...props}
          onNodeUpdate={handleNodeUpdate}
          borderColor={layerColor.package}
          showQRCode={showQRCode}
        />
      ),
    }),
    [handleNodeUpdate, layerColor, showQRCode]
  );

  const getNextNodeType = (currentType: string): string | null => {
    if (currentType === "building") return "zone";
    if (currentType === "zone") return "batch";
    if (currentType === "batch") return "package";
    return null;
  };

  const validateConnection = useCallback(
    (sourceNode: Node, targetNode: Node) => {
      // Check if target node already has a parent
      const targetHasParent = edges.some(
        (edge) => edge.target === targetNode.id
      );
      if (targetHasParent) {
        alert("此節點已經有父節點，無法再添加父節點");
        return false;
      }

      // Check if connection is valid based on node types
      const validChildren = validConnections[sourceNode.type];
      if (!validChildren || !validChildren.includes(targetNode.type)) {
        alert(`無效的連接: ${sourceNode.type} 不能連接到 ${targetNode.type}`);
        return false;
      }

      return true;
    },
    [edges]
  );

  const onConnect = useCallback(
    async (params: { source: string; target: string }) => {
      const sourceNode = nodes.find(
        (node) => node.id === params.source
      ) as Node;
      const targetNode = nodes.find(
        (node) => node.id === params.target
      ) as Node;

      if (!sourceNode || !targetNode) {
        alert("找不到源節點或目標節點");
        return;
      }
      if (validateConnection(sourceNode, targetNode)) {
        const newEdge = { ...params, id: `${params.source}-${params.target}` };
        setEdges((eds) => addEdge(newEdge, eds));

        // Update both production plan and project nodes
        try {
          // Update production plan
          await updateProject({
            id: id || "",
            productionPlan: {
              nodes: nodes.map((node) => {
                const { designBOM, ...dataWithoutDesignBOM } = node.data;
                return {
                  id: node.id,
                  documentId: node.documentId,
                  type: node.type,
                  position: node.position,
                  data: {
                    ...dataWithoutDesignBOM,
                  },
                };
              }),
              edges: [...edges, newEdge],
            },
          });

          if (sourceNode.documentId && targetNode.documentId) {
            // Note: childNodes relation is automatically managed by the database via parentNodeId
            // No need to update source node's childNodes manually

            // Update target node to set its parent
            await updateProjectNode({
              id: targetNode.documentId,
              parentNodeId: sourceNode.documentId,
              name: targetNode.data.label,
            });
          }
        } catch (error) {
          console.error("Error updating after edge creation:", error);
          // Revert the edge if update fails
          setEdges((eds) => eds.filter((edge) => edge.id !== newEdge.id));
        }
      }
    },
    [nodes, edges, validateConnection, id]
  );

  const onConnectEnd = useCallback<OnConnectEnd>(
    async (event, connectionState) => {
      if (!connectionState.isValid) {
        const sourceNodeId = connectionState.fromNode?.id;
        if (!sourceNodeId) return;

        const sourceNode = nodes.find((node) => node.id === sourceNodeId);
        if (!sourceNode) return;

        const nextNodeType = getNextNodeType(sourceNode.type) as NodeType;
        if (!nextNodeType) {
          alert("無法從此節點創建子節點");
          return;
        }

        // Increment the child count for unique naming
        if (!childCounts[sourceNodeId]) {
          childCounts[sourceNodeId] = 1;
        } else {
          childCounts[sourceNodeId]++;
        }

        const newNodeName = `${t(`productionPlanFlow.${nextNodeType}`)}${childCounts[sourceNodeId]}`;

        const { clientX, clientY } =
          "changedTouches" in event
            ? event.changedTouches[0]
            : (event as MouseEvent);
        const position = screenToFlowPosition({ x: clientX, y: clientY });

        let newNodeId: string | undefined;
        let newEdge: Edge | undefined;

        try {
          // Use the source node's documentId as the parent node ID
          const parentNodeId = sourceNode.documentId;
          console.log("Creating new node with:", {
            sourceNode,
            parentNodeId,
            nextNodeType,
            projectId: id,
          });

          if (!parentNodeId) {
            throw new Error("Source node has no documentId");
          }

          if (parentNodeId === "0") {
            throw new Error(
              "Parent node still has placeholder ID '0'. Please refresh the page."
            );
          }

          let projectNodeResult = await createProjectNode({
            projectId: id!,
            nodeType: nextNodeType,
            parentNodeId: parentNodeId,
            name: newNodeName,
          });

          if (!projectNodeResult?.id) {
            throw new Error("Failed to create new node");
          }

          newNodeId = projectNodeResult.id;
          const newNode: Node = {
            id: projectNodeResult.id,
            documentId: projectNodeResult.id,
            type: nextNodeType,
            position,
            data: {
              label: newNodeName,
              name: newNodeName,
              type: nextNodeType,
              documentId: projectNodeResult.id,
              parentNode: parentNodeId,
              childNodes: [],
              workItems: [],
              steelAssemblyList: [],
              steelAssemblyStats: {},
              designBOM: designBomData,
            },
            origin: nodeOrigin,
          };

          // Create edge between source and new node
          newEdge = {
            id: `${sourceNodeId}-${newNodeId}`,
            source: sourceNodeId,
            target: newNodeId,
          };

          // Update nodes and edges state
          setNodes((nds) => {
            // Update the parent node's childNodes array
            const updatedNodes = nds.map((node) => {
              if (node.documentId === parentNodeId) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    childNodes: [
                      ...(node.data.childNodes || []),
                      newNodeId,
                    ].filter((id): id is string => id !== undefined),
                  },
                };
              }
              return node;
            });
            return [...updatedNodes, newNode];
          });
          setEdges((eds) => addEdge(newEdge!, eds));
          setNodeIdMap((prev) => ({ ...prev, [newNodeId]: newNodeId }));

          // Note: childNodes relation is automatically managed via parentNodeId
          // No backend update needed for parent node

          // Update production plan
          await updateProject({
            id: id || "",
            productionPlan: {
              nodes: [...nodes, newNode].map((node) => {
                const { designBOM, ...dataWithoutDesignBOM } = node.data;
                // If this is the parent node, update its childNodes
                if (node.documentId === parentNodeId) {
                  return {
                    ...node,
                    data: {
                      ...dataWithoutDesignBOM,
                      childNodes: [
                        ...(node.data.childNodes || []),
                        newNodeId,
                      ].filter((id): id is string => id !== undefined),
                    },
                  };
                }
                return {
                  ...node,
                  documentId: node.documentId,
                  type: node.type,
                  position: node.position,
                  data: {
                    ...dataWithoutDesignBOM,
                    documentId: node.documentId,
                    parentNode: node.data.parentNode,
                    childNodes: node.data.childNodes || [],
                    workItems: node.data.workItems || [],
                    steelAssemblyList: node.data.steelAssemblyList || [],
                    steelAssemblyStats: node.data.steelAssemblyStats || {},
                  },
                };
              }),
              edges: [...edges, newEdge],
            },
          });
        } catch (error) {
          console.error("Error creating new node:", error);
          // Revert changes if update fails
          if (newNodeId) {
            setNodes((nds) => nds.filter((node) => node.id !== newNodeId));
          }
          if (newEdge) {
            setEdges((eds) => eds.filter((edge) => edge.id !== newEdge.id));
          }
        }
      }
    },
    [screenToFlowPosition, nodes, edges, nodeIdMap, id, designBomData]
  );

  const handleSelectionChange = useCallback(
    ({ nodes: selectedNodes }: { nodes: any[]; edges: any[] }) => {
      if (!onSelectedNodeChange) return;
      const node = selectedNodes?.[0];
      if (!node) {
        onSelectedNodeChange(null);
        return;
      }
      onSelectedNodeChange({
        id: node.documentId || node.data?.documentId || node.id,
        name: node.data?.label || node.data?.name || "",
        type: node.type,
      });
    },
    [onSelectedNodeChange]
  );

  const onNodesDelete = useCallback(
    async (deletedNodes: Node[]) => {
      // Filter out the root node to prevent deletion
      const deletableNodes = deletedNodes.filter((node) => {
        // Check if it's the root node - either by ID "0" or building type without parent
        const isRootNode =
          node.id === "0" ||
          (node.type === "building" && !node.data.parentNode);
        return !isRootNode;
      });

      // If no nodes can be deleted, return early
      if (deletableNodes.length === 0) {
        return;
      }

      // Get all edges connected to the deletable nodes
      const connectedEdges = getConnectedEdges(deletableNodes, edges);

      const deletedNodeIds = new Set(
        deletableNodes.flatMap((dn) =>
          [dn.id, dn.documentId, (dn.data as any)?.documentId].filter(Boolean)
        )
      );
      const dropDeletedChildren = (node: Node): Node => {
        const childNodes = ((node.data as any)?.childNodes || []) as string[];
        const kept = childNodes.filter((childId) => !deletedNodeIds.has(childId));
        if (kept.length === childNodes.length) return node;
        return { ...node, data: { ...node.data, childNodes: kept } };
      };

      // Remove the deletable nodes and their connected edges
      setNodes((nds) =>
        nds
          .filter((node) => !deletableNodes.some((dn) => dn.id === node.id))
          .map(dropDeletedChildren)
      );
      setEdges((eds) =>
        eds.filter((edge) => !connectedEdges.some((ce) => ce.id === edge.id))
      );

      // Update both production plan and project nodes
      try {
        await updateProject({
          id,
          productionPlan: {
            nodes: nodes
              .filter((node) => !deletableNodes.some((dn) => dn.id === node.id))
              .map(dropDeletedChildren)
              .map((node) => {
                const { designBOM, ...dataWithoutDesignBOM } = node.data;
                return {
                  id: node.id,
                  documentId: node.documentId,
                  type: node.type,
                  position: node.position,
                  data: {
                    ...dataWithoutDesignBOM,
                  },
                };
              }),
            edges: edges.filter(
              (edge) => !connectedEdges.some((ce) => ce.id === edge.id)
            ),
          },
        });

        // Delete project nodes
        for (const node of deletableNodes) {
          if (node.documentId) {
            await deleteProjectNode({
              id: node.documentId,
            });
          }
        }
      } catch (error) {
        console.error("Error updating after deletion:", error);
        // Revert the changes if update fails
        setNodes(nodes);
        setEdges(edges);
      }
    },
    [nodes, edges, id]
  );

  const onEdgesDelete = useCallback(
    async (deletedEdges: Edge[]) => {
      setEdges((eds) =>
        eds.filter((edge) => !deletedEdges.some((de) => de.id === edge.id))
      );

      const detachedChildIds = new Set(deletedEdges.map((edge) => edge.target));
      const detachedParentIds = new Set(deletedEdges.map((edge) => edge.source));
      const detachNode = (node: Node): Node => {
        if (detachedChildIds.has(node.id)) {
          return { ...node, data: { ...node.data, parentNode: null } };
        }
        if (detachedParentIds.has(node.id)) {
          const childNodes = ((node.data as any)?.childNodes || []) as string[];
          return {
            ...node,
            data: {
              ...node.data,
              childNodes: childNodes.filter(
                (childId) => !detachedChildIds.has(childId)
              ),
            },
          };
        }
        return node;
      };

      setNodes((nds) => nds.map(detachNode));

      // Update both production plan and project nodes
      try {
        // Update production plan
        await updateProject({
          id,
          productionPlan: {
            nodes: nodes.map(detachNode).map((node) => {
              const { designBOM, ...dataWithoutDesignBOM } = node.data;
              return {
                id: node.id,
                documentId: node.documentId,
                type: node.type,
                position: node.position,
                data: {
                  ...dataWithoutDesignBOM,
                },
              };
            }),
            edges: edges.filter(
              (edge) => !deletedEdges.some((de) => de.id === edge.id)
            ),
          },
        });

        // Update project nodes to remove parent-child relationships
        for (const edge of deletedEdges) {
          const targetNode = nodes.find((node) => node.id === edge.target);

          if (targetNode?.documentId) {
            // Update target node to remove its parent
            await updateProjectNode({
              id: targetNode.documentId,
              parentNodeId: null,
            });
          }
        }
      } catch (error) {
        console.error("Error updating after edge deletion:", error);
        // Revert the edges if update fails
        setNodes(nodes);
        setEdges(edges);
      }
    },
    [nodes, edges, id]
  );

  const handleUpdateNodesJSON = async (nodeJsonData: NodeJsonData) => {
    if (nodeJsonData?.nodes) {
      const updatedNodes = nodeJsonData.nodes.map((node) => ({
        ...node,
        documentId: node.documentId || node.id,
        data: {
          ...node.data,
          documentId: node.documentId || node.id,
          childNodes: node.data.childNodes || [],
        },
      }));
      setNodes(updatedNodes);
    }
    if (nodeJsonData?.edges) {
      setEdges(nodeJsonData.edges);
    }

    const mapping: Record<string, string> = {};
    for (const node of nodeJsonData?.nodes || []) {
      if (node?.documentId) {
        mapping[node.documentId] = node.documentId;
      }
    }
    setNodeIdMap(mapping);
  };

  // const fetchDesignBomData = async (page = 1) => {
  //   try {
  //     let result = await apiUserGetDesignBOMs({ page });

  //     let data: DesignBOM[] = [];

  //     const tempPayableList = await Promise.all(
  //       result?.data.map(async (t: DesignBOM) => {
  //         const result = await apiUserGetDesignBOM({ id: t.documentId });
  //         data.push(result?.data);
  //         return result?.data;
  //       }),
  //     );
  //     setFileUpload(tempPayableList);
  //     setDesignBOM(result);
  //   } catch (error) {
  //     console.error('Error fetching department data:', error);
  //   }
  // };

  useEffect(() => {
    const fetchData = async () => {
      if (!isInitialLoad) return;
      try {
        // Fetch project nodes from backend using orpc
        const projectNodes = await orpcClient.projectNode.list({
          filter: { projectId: id! },
        });

        // Find the building node (root node)
        const buildingNode = projectNodes.find(
          (node) => node.nodeType === "building"
        );

        if (!buildingNode) {
          console.error("No building node found for project");
          return;
        }

        // Fetch the project data for productionPlan
        let result = await single(orpcClient.project.read([{ id: id! }]));
        const productionPlan = (result?.productionPlan ?? null) as NodeJsonData | null;

        if (productionPlan?.nodes || projectNodes.length > 0) {
          // Build nodes from ALL project nodes, not just production plan
          const allNodes: Node[] = projectNodes.map((projectNode: any) => {
            // Try to find existing position from production plan
            const existingNode = productionPlan?.nodes?.find(
              (n: Node) =>
                n.documentId === projectNode.id || n.id === projectNode.id
            );

            // Calculate position based on hierarchy if no existing position
            let position = existingNode?.position || { x: 0, y: 50 };
            if (!existingNode && projectNode.parentNodeId) {
              // Offset child nodes below and to the right
              const depth =
                projectNode.nodeType === "zone"
                  ? 1
                  : projectNode.nodeType === "batch"
                    ? 2
                    : 3;
              position = { x: depth * 200, y: depth * 150 };
            }

            return {
              id: projectNode.id,
              documentId: projectNode.id,
              type: projectNode.nodeType as NodeType,
              position,
              data: {
                label:
                  projectNode.name ||
                  defaultNodeName[projectNode.nodeType as NodeType],
                name:
                  projectNode.name ||
                  defaultNodeName[projectNode.nodeType as NodeType],
                type: projectNode.nodeType,
                documentId: projectNode.id,
                parentNode: projectNode.parentNodeId,
                childNodes:
                  projectNode.childNode?.map((child: any) => child.id) || [],
                workItems: [],
                steelAssemblyList: [],
                steelAssemblyStats: {},
                ...(existingNode?.data?.priority !== undefined
                  ? { priority: existingNode.data.priority }
                  : {}),
                ...(existingNode?.data?.startTime !== undefined
                  ? { startTime: existingNode.data.startTime }
                  : {}),
                ...(existingNode?.data?.endTime !== undefined
                  ? { endTime: existingNode.data.endTime }
                  : {}),
              },
            };
          });

          // Build edges from parent-child relationships
          const allEdges: Edge[] = projectNodes
            .filter((node: any) => node.parentNodeId)
            .map((node: any) => ({
              id: `${node.parentNodeId}-${node.id}`,
              source: node.parentNodeId,
              target: node.id,
            }));

          handleUpdateNodesJSON({
            nodes: allNodes,
            edges: allEdges,
          });
          setIsInitialLoad(false);

          const storedPlan = productionPlan;
          const rebuiltPlanNodes = allNodes.map((node) => {
            const { designBOM, ...dataWithoutDesignBOM } = node.data as any;
            return {
              id: node.id,
              documentId: node.documentId,
              type: node.type,
              position: node.position,
              data: {
                ...dataWithoutDesignBOM,
              },
            };
          });

          if (
            planSignature(storedPlan?.nodes || [], storedPlan?.edges || []) !==
            planSignature(rebuiltPlanNodes, allEdges)
          ) {
            try {
              await updateProject({
                id: id || "",
                productionPlan: {
                  nodes: rebuiltPlanNodes,
                  edges: allEdges,
                },
              });
            } catch (error) {
              console.error("Error syncing production plan:", error);
            }
          }
        } else {
          // If no production plan exists, create initial building node
          console.log(
            "No production plan found, using building node:",
            buildingNode
          );
          const initialNode: Node = {
            id: buildingNode.id,
            documentId: buildingNode.id,
            type: "building",
            position: { x: 0, y: 50 },
            data: {
              label: buildingNode.name || "整體建築",
              name: buildingNode.name || "整體建築",
              type: "building",
              documentId: buildingNode.id,
              parentNode: null,
              childNodes: [],
              workItems: [],
              steelAssemblyList: [],
              steelAssemblyStats: {},
            },
          };

          setNodes([initialNode]);
          setEdges([]);
          setIsInitialLoad(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [isInitialLoad]);

  useEffect(() => {
    if (!reloadKey) return;
    setIsInitialLoad(true);
  }, [reloadKey]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // useEffect(() => {
  //   fetchDesignBomData();
  // }, []);

  // useEffect(() => {
  //   const fetchSteelAssemblyData = async () => {
  //     try {
  //       const steelAssemblyNodes = nodes.filter((node) => node.type === 'package');

  //       const updatedNodes = await Promise.all(
  //         steelAssemblyNodes.map(async (node) => {
  //           if (node.documentId) {
  //             const result = await apiUserGetAccountsProjectNode({ id: node.documentId });
  //             if (result?.data) {
  //               return {
  //                 ...node,
  //                 data: {
  //                   ...node.data,
  //                   steelAssemblyList: result.data.steelAssemblyList || [],
  //                   steelAssemblyStats: result.data.steelAssemblyStats || {},
  //                 },
  //               };
  //             }
  //           }
  //           return node;
  //         }),
  //       );

  //       setNodes((prevNodes) =>
  //         prevNodes.map((node) => {
  //           const updatedNode = updatedNodes.find((n) => n.id === node.id);
  //           return updatedNode || node;
  //         }),
  //       );
  //     } catch (error) {
  //       console.error('Error fetching steel assembly data:', error);
  //     }
  //   };

  //   fetchSteelAssemblyData();
  // }, [nodes.length]); // Only run when number of nodes changes

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      )
        return;

      const selectedNodes = nodes.filter((n) => (n as any).selected);
      const selectedEdges = edges.filter((e) => (e as any).selected);

      if (selectedNodes.length === 0 && selectedEdges.length === 0) return;

      e.preventDefault();
      e.stopPropagation();

      setPendingDeleteNodes(selectedNodes);
      setPendingDeleteEdges(selectedEdges);
      setDeleteConfirmOpen(true);
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [nodes, edges]);

  return (
    <div className="wrapper" ref={reactFlowWrapper} style={{ height: "92dvh" }}>
      <ReactFlow
        style={{ backgroundColor: "#F7F9FB" }}
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onConnectEnd={onConnectEnd}
        onNodesDelete={onNodesDelete}
        onEdgesDelete={onEdgesDelete}
        onSelectionChange={handleSelectionChange}
        fitView
        fitViewOptions={{ padding: 2 }}
        nodeOrigin={nodeOrigin}
        nodeTypes={memoizedNodeTypes}
        deleteKeyCode={null}
      >
        <Background />
      </ReactFlow>
      <Modal
        open={deleteConfirmOpen}
        onOk={async () => {
          setDeleteConfirmOpen(false);
          if (pendingDeleteNodes.length > 0) {
            await onNodesDelete(pendingDeleteNodes);
          }
          if (pendingDeleteEdges.length > 0) {
            await onEdgesDelete(pendingDeleteEdges);
          }
          setPendingDeleteNodes([]);
          setPendingDeleteEdges([]);
        }}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteNodes([]);
          setPendingDeleteEdges([]);
        }}
        title={t("productionPlanFlow.deleteConfirmTitle")}
        okText={t("productionPlanFlow.deleteConfirmTitle")}
        cancelText={t("productionPlanFlow.cancel")}
        okType="danger"
      >
        <p>{t("productionPlanFlow.deleteConfirmMessage")}</p>
      </Modal>
      <Modal
        open={isPriorityModalOpen}
        title={t("productionPlanFlow.priorityMode")}
        onOk={savePriorities}
        onCancel={() => setIsPriorityModalOpen(false)}
        okText={t("productionPlanFlow.prioritySave")}
        cancelText={t("productionPlanFlow.cancel")}
        width={640}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
            <span style={{ flex: 1 }} />
            <span style={{ width: 64, flexShrink: 0, textAlign: "center" }}>{t("productionPlanFlow.priorityMode")}</span>
            <span style={{ width: 170, flexShrink: 0, textAlign: "center" }}>{t("productionPlanFlow.startTime")}</span>
            <span style={{ width: 170, flexShrink: 0, textAlign: "center" }}>{t("productionPlanFlow.endTime")}</span>
          </div>
          {(["building", "zone", "batch", "package"] as NodeType[]).map((type) => {
            const groupNodes = nodes
              .filter((node) => node.type === type)
              .sort((a, b) => (a.position?.x ?? 0) - (b.position?.x ?? 0));
            if (groupNodes.length === 0) return null;
            return (
              <div key={type} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Divider orientation="left" style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
                  {t(`productionPlanFlow.${type}`)}
                </Divider>
                {groupNodes.map((node) => (
                  <div key={node.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {node.data.label}
                    </span>
                    <input
                      type="number"
                      min={1}
                      value={priorityInputs[node.id] ?? ""}
                      onChange={(e) =>
                        setPriorityInputs((prev) => ({ ...prev, [node.id]: e.target.value }))
                      }
                      style={{
                        width: 64,
                        border: "1px solid #d1d5db",
                        borderRadius: 4,
                        padding: "3px 8px",
                        fontSize: 14,
                        flexShrink: 0,
                      }}
                    />
                    <DatePicker
                      format="YYYY-MM-DD"
                      size="small"
                      style={{ width: 170, flexShrink: 0 }}
                      value={startTimeInputs[node.id] ? dayjs(startTimeInputs[node.id]) : null}
                      onChange={(value) =>
                        setStartTimeInputs((prev) => ({
                          ...prev,
                          [node.id]: value ? value.format("YYYY-MM-DD") : "",
                        }))
                      }
                    />
                    <DatePicker
                      format="YYYY-MM-DD"
                      size="small"
                      style={{ width: 170, flexShrink: 0 }}
                      value={endTimeInputs[node.id] ? dayjs(endTimeInputs[node.id]) : null}
                      onChange={(value) =>
                        setEndTimeInputs((prev) => ({
                          ...prev,
                          [node.id]: value ? value.format("YYYY-MM-DD") : "",
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
};
export default ProductionPlanFlow;
